import React, { ReactElement, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Image, Button, Stack } from 'react-bootstrap';
import { map } from 'lodash';
import pluralize from 'pluralize';
import { useTranslation } from 'react-i18next';

import { BackButton, ClaimBadge } from '@components/Base';
import { PaymentMethods } from '@models/PaymentInfo';
import { ClaimableTypes } from '@models/InsuranceClaim';
import { formatDateTime } from '@utils/datetime';
import { amountToUsd } from '@utils/currency';
import { getAttachmentType, isAttachmentImage, isAttachmentPdf } from '@utils/attachment';
import { tCarClaimType } from '@models/CarClaim';

import { selectCarClaim } from './selector';
import { carClaimFetch } from './action';
import { useEditable } from '../hook';
import { InsuranceClaimEditModal } from '../../InsuranceClaimEditModal';

interface RowItemProps {
  label: string;
  value?: string | number | ReactElement | null;
  list?: string[];
  renderItem?: (item: string) => void;
}

const RowItem = ({ label, value, list, renderItem }: RowItemProps) => {
  return (
    <Row className="mb-2">
      <Col xs={4} sm={4} md={3}>
        {label}
      </Col>
      <Col xs="auto">:</Col>
      <Col>
        <>
          {value && value}
          {list && renderItem && map(list, renderItem)}
        </>
      </Col>
    </Row>
  );
};

interface ClaimAttachmentItemProps {
  name: string;
  url: string;
}

const ClaimAttachmentItem = ({ name, url }: ClaimAttachmentItemProps) => {
  const { t } = useTranslation();
  const attachmentType = getAttachmentType(url);

  return (
    <>
      {isAttachmentImage(attachmentType) && (
        <Image src={url} thumbnail className="mb-3" data-testid={`${name}-image`} />
      )}

      {isAttachmentPdf(attachmentType) && (
        <Link to={url} className="d-block mb-3" target="_blank" rel="noreferrer" data-testid={`${name}-pdf`}>
          {t('common:clickToViewDetails')}
        </Link>
      )}
    </>
  );
};

const CarClaimDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { claimableType, claimableId } = useParams();
  const { carClaim } = useSelector(selectCarClaim);
  const shouldEdit = useEditable(carClaim);
  const isPaymentByBankTransfer = carClaim?.paymentInfo?.paymentMethod === PaymentMethods.BankTransfer;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleReload = () => {
    dispatch(carClaimFetch({ carClaimId: claimableId }));
  };

  useEffect(() => {
    dispatch(carClaimFetch({ carClaimId: claimableId }));
  }, [claimableId]);

  return (
    <>
      <BackButton className="mb-4" />
      {carClaim && (
        <Card>
          <Card.Header>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <h5 className="mb-0 me-auto">{t('common:details')}</h5>
              {shouldEdit && (
                <Button size="sm" onClick={() => setIsModalOpen(true)}>
                  {t('common:edit')}
                </Button>
              )}
            </Stack>
          </Card.Header>

          <Card.Body>
            <RowItem label={t('common:status')} value={<ClaimBadge status={carClaim.status} />} />
            <RowItem label={t('common:claimNumber')} value={carClaim.claimNumber} />
            <RowItem
              label={t('carInsuranceClaimForm.accidentHappenedAt.label')}
              value={formatDateTime(carClaim.accidentHappenedAt)}
            />
            <RowItem
              label={t('carInsuranceClaim.accidentPhotos')}
              list={carClaim.accidentPhotos}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="accident-photos" url={url} />}
            />
            <RowItem
              label={pluralize(t('claimForm.claimType.label'), 2)}
              list={carClaim.claimTypes}
              renderItem={(claimType) => (
                <span className="d-block" key={claimType}>
                  - {tCarClaimType(claimType)}
                </span>
              )}
            />
            <RowItem
              label={t('carInsuranceClaim.policeReport')}
              list={carClaim.passengersAndThirdPartiesPoliceReports}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="police-reports" url={url} />}
            />
            <RowItem
              label={t('carInsuranceClaim.supportingDocuments')}
              list={carClaim.supportingDocuments}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="supporting-documents" url={url} />}
            />
            <RowItem
              label={t('common:additionalDocuments')}
              list={carClaim.additionalDocuments}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="additional-documents" url={url} />}
            />
            <RowItem label={t('claimForm.totalClaimedAmount.label')} value={amountToUsd(carClaim.totalClaimedAmount)} />
            <RowItem label={t('claimForm.approvedAmount.label')} value={amountToUsd(carClaim.approvedAmount)} />
            <RowItem label={t('common:note')} value={carClaim.note} />
            <hr />
            <RowItem label={t('paymentInfo.paymentMethod')} value={carClaim.paymentInfo?.paymentMethod} />
            <RowItem label={t('paymentInfo.beneficiaryName')} value={carClaim.paymentInfo?.beneficiaryName} />
            {isPaymentByBankTransfer && (
              <>
                <RowItem label={t('paymentInfo.bankName')} value={carClaim.paymentInfo?.bankName} />
                <RowItem label={t('paymentInfo.bankAccount')} value={carClaim.paymentInfo?.bankAccount} />
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {carClaim && (
        <InsuranceClaimEditModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          claimNumber={carClaim.claimNumber}
          claimableType={ClaimableTypes.Car}
          claimableId={carClaim.id}
          currentStatus={carClaim.status}
          currentTotalClaimedAmount={carClaim.totalClaimedAmount}
          currentNote={carClaim.note}
          currentApprovedAmount={carClaim.approvedAmount}
          onSuccess={handleReload}
        />
      )}
    </>
  );
};

export { CarClaimDetails };
